/**
 * Reading Phase View (Web)
 *
 * Renders the saccadic scroll reading experience with glass overlays,
 * focus blur, and convergence shrink effects.
 */

import React, { memo } from 'react';
import { View, Text, Animated, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import type { AnimationPhase } from './types';
import { Y2K_GRADIENT_COLORS, FOCUS_BLUR_LEVELS } from './types';
import { styles } from './animationStyles.web';

interface ReadingPhaseViewProps {
  phase: AnimationPhase;
  readingLines: string[];
  currentLineIndex: number;
  readingScrollY: number;
  convergenceScale: number;
  convergenceBlurProgress: number;
  textOpacity: Animated.Value;
  paddingLines: number;
  lineHeight: number;
}

const ReadingPhaseView = memo(function ReadingPhaseView({
  phase,
  readingLines,
  currentLineIndex,
  readingScrollY,
  convergenceScale,
  convergenceBlurProgress,
  textOpacity,
  paddingLines,
  lineHeight,
}: ReadingPhaseViewProps) {
  if (phase !== 'reading' && phase !== 'convergence' && phase !== 'complete') {
    return null;
  }

  const glassOpacity = phase === 'reading' ? 1 : 0;

  const getFocusLevel = (lineIdx: number): 0 | 1 | 2 | 3 | 4 => {
    const distance = Math.abs(lineIdx - currentLineIndex);
    return Math.min(distance, 4) as 0 | 1 | 2 | 3 | 4;
  };

  return (
    <>
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(10, 0, 18, 0.95)', 'rgba(10, 0, 18, 0)']}
        style={[styles.glassOverlayTop, { opacity: glassOpacity }]}
      />
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(10, 0, 18, 0)', 'rgba(10, 0, 18, 0.95)']}
        style={[styles.glassOverlayBottom, { opacity: glassOpacity }]}
      />
      <LinearGradient
        pointerEvents="none"
        colors={[Y2K_GRADIENT_COLORS.pink + '10', Y2K_GRADIENT_COLORS.lavender + '20', Y2K_GRADIENT_COLORS.mint + '10']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={[styles.focusGlowZone, { opacity: glassOpacity }]}
      />

      <Animated.View
        pointerEvents={phase === 'complete' ? 'none' : 'auto'}
        style={[
          styles.readingContainer,
          {
            opacity: textOpacity,
            transform: [
              { translateY: readingScrollY },
              { scale: convergenceScale },
            ],
          },
        ]}
      >
        {readingLines.map((line, idx) => {
          const isPaddingLine = idx < paddingLines || idx >= readingLines.length - paddingLines;
          const focusLevel = getFocusLevel(idx);
          const focusStyle = FOCUS_BLUR_LEVELS[focusLevel];
          const effectiveBlur = focusStyle.blur * (1 - convergenceBlurProgress);
          return (
            <Text
              key={`line-${idx}`}
              style={[
                styles.readingLine,
                {
                  height: lineHeight,
                  opacity: isPaddingLine ? focusStyle.opacity * 0.3 : focusStyle.opacity,
                  transform: [{ scale: focusStyle.scale }],
                  ...(Platform.OS === 'web' ? {
                    filter: `blur(${effectiveBlur}px)`,
                  } : {}),
                },
                focusLevel === 0 && !isPaddingLine && styles.readingLineFocused,
              ]}
            >
              {line}
            </Text>
          );
        })}
      </Animated.View>
    </>
  );
});

export default ReadingPhaseView;
